import { Admin } from '@app/client';
import { apiReturnPaginatedResults } from '@app/client/apiFactory/apiReturn';
import { setError } from '@app/store/actions/error';

import { apiFactory } from '../../apiFactory/api';
import {
    type AdminUpdateUser,
    type ApiResult,
    type BaseParams,
    type DisplayUser,
    type DisplayUserExtended,
    type PaginatedResult,
    type PagingParams,
    type PostParams,
    type RegisterUser,
    Request,
    type ResourceId,
    type Roles,
    type UserAccount,
    type UserData,
    type UserDataKey,
} from '../../common';
import { links } from '../../links';

export interface AdminRegisterUser extends RegisterUser {
    alternate_id?: string;
    source?: string;
    activate?: boolean;
    activation_source?: string;
}

export interface SyncNftWallet {
    wallet_address: string;
}

export interface AdminDeleteUserResponse {
    alternate_id?: string;
    created_at: Date;
    deactivated_at: Date;
    email: string;
    email_verified_at?: Date;
    first_name?: string;
    free_limit: number;
    id: string;
    last_name?: string;
    pubkeys: string[];
    remaining_free_emoji: 1;
    role: Roles;
    source: string;
    two_factor_auth: string;
    two_factor_last_prompted_at?: Date;
    two_factor_should_prompt: boolean;
    updated_at: Date;
}

export async function getCollection(
    params: BaseParams<PagingParams>,
): Promise<ApiResult<PaginatedResult<DisplayUserExtended>>> {
    return Request.getRequest<PaginatedResult<DisplayUserExtended>>({
        ...params,
        target: links.user().collection(),
    });
}

export async function update(
    params: BaseParams & PostParams<AdminUpdateUser> & { id: ResourceId },
): Promise<ApiResult<DisplayUser>> {
    return Request.patchRequest<DisplayUser>({
        ...params,
        target: links.user().single(params.id),
        settings: {
            ...params.settings,
        },
    });
}

export async function view(params: BaseParams & { id: ResourceId }): Promise<ApiResult<DisplayUserExtended>> {
    return Request.getRequest<DisplayUserExtended>({
        ...params,
        target: links.user().single(params.id),
        settings: {
            ...params.settings,
        },
    });
}

export async function registerUser(params: BaseParams & PostParams<AdminRegisterUser>) {
    return Request.postRequest<UserAccount>({
        ...params,
        target: links.user().collection(),
        settings: {
            ...params.settings,
        },
    });
}

export async function deleteUser(params: BaseParams & { id: ResourceId }) {
    return Request.deleteRequest<AdminDeleteUserResponse>({
        ...params,
        target: links.user().single(params.id),
    });
}

export async function syncNftWallet(params: BaseParams & PostParams<SyncNftWallet> & { id: ResourceId }) {
    return Request.postRequest({
        ...params,
        target: links.admin().wallets(params.id).syncNftWallet(),
    });
}

type UserDataParams = BaseParams & { key: UserDataKey; id: ResourceId };
export async function getUserData(params: UserDataParams): Promise<ApiResult<UserData | null>> {
    return Request.getRequest<UserData | null>({
        ...params,
        target: links.admin().users().data(params.key, params.id),
    });
}

export const callGetUserData = (params): UserData['value'] => {
    return apiFactory()
        .call(getUserData, { key: params.key, id: params.id })
        .then((r) => {
            if (r.ok && r.value && r.value.value) {
                return r.value.value;
            }
        })
        .catch((e) => {
            setError({
                ...e,
                log: true,
                source: 'Error getting user data.',
            })();
            return e;
        });
};

export type SetUserDataParams = BaseParams & { key: UserDataKey; id: ResourceId } & PostParams<{ value: unknown }>;

export async function putUserData(params: SetUserDataParams): Promise<ApiResult<UserData | null>> {
    return Request.putRequest<UserData | null>({
        ...params,
        target: links.admin().users().data(params.key, params.id),
    });
}

export const callSetUserData = (params: SetUserDataParams) => {
    return apiFactory()
        .call(putUserData, {
            ...params,
        })
        .then((r) => {
            if (r.ok) {
                return r.value;
            } else {
                alert(r.error);
            }
        })
        .catch((e) => {
            setError({
                ...e,
                log: true,
                source: 'Error setting user data.',
            })();
            return e;
        });
};

type CallGetUsersType = BaseParams & {
    userQuery?: string;
    page?: number;
    limit?: number;
};

export const callGetUsers = (params: CallGetUsersType) =>
    apiReturnPaginatedResults<DisplayUserExtended>(Admin.Users.getCollection, {
        ...params,
        query: {
            q: params.userQuery,
            page: params.page,
            limit: params.limit,
        },
    });
